import React, { useState } from 'react';
import { Button, Input } from '../ui';
import closeIcon from '../../assets/images/close.png';
import { useThemeContext } from '../../contexts/ThemeContext';

interface JoinRecordModalProps {
  onClose: () => void;
  onJoin: (code: string) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
}

const JoinRecordModal: React.FC<JoinRecordModalProps> = ({ 
  onClose, 
  onJoin, 
  isLoading 
}) => {
  const { isDark } = useThemeContext();
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string>('');

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (value: string) => {
    // Only allow numbers
    const numericValue = value.replace(/\D/g, '');
    setInviteCode(numericValue);
    
    // Clear error when user types
    if (error) {
      setError('');
    }
  };

  const validateForm = (): boolean => {
    if (!inviteCode.trim()) {
      setError('Invite code is required');
      return false;
    }

    if (inviteCode.length < 4) {
      setError('Invite code must be at least 4 digits');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const result = await onJoin(inviteCode);

    if (result.success) {
      onClose();
    } else {
      setError(result.error || 'Failed to join record');
    }
  };

  const getIcon = (lightIcon: string, darkIcon: string) => {
    return isDark ? darkIcon : lightIcon;
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-700 bg-opacity-50 p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-light rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 border-b border-grey_light">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-dark">Join Record</h3>
              <p className="text-grey_dark mt-1">Enter invite code to join a record</p>
            </div>
            <button
              onClick={onClose}
              className="text-grey_dark hover:text-dark p-2"
              aria-label="Close"
              disabled={isLoading}
            >
              <img
                src={closeIcon}
                alt="Close"
                className="w-6 h-6"
              />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-dark mb-2">
                Invite Code *
              </label>
              <Input
                type="text"
                attributes={{
                  placeholder: "Enter 6-digit invite code",
                  value: inviteCode,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e.target.value),
                  disabled: isLoading,
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
                // error={!!error}
              />
              {error && (
                <p className="text-danger text-sm mt-1">{error}</p>
              )}
              <p className="text-grey_dark text-xs mt-1">
                Enter the 6-digit code shared by the record owner
              </p>
            </div>

            <div className="border-t border-grey_light pt-4">
              <div className="bg-bg_grey_light p-3 rounded-lg">
                <h4 className="font-medium text-dark mb-2">How to get an invite code</h4>
                <ul className="text-sm text-grey_dark space-y-2">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Ask the record owner to share their invite code</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>The code is usually 6 digits (e.g., 123456)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Once joined, you'll have access to the record based on permissions granted</span>
                  </li>
                </ul>
              </div>
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-grey_light bg-bg_grey_light">
          <div className="flex justify-end space-x-3">
            <Button
              onClick={onClose}
              variant="bordered-primary"
              className="px-4 py-2"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="filled-primary"
              className="px-4 py-2"
              disabled={isLoading || !inviteCode.trim()}
            >
              {isLoading ? 'Joining...' : 'Join Record'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinRecordModal;